import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretLeft, CaretRight, Quotes } from '@phosphor-icons/react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

export const TestimonialCarousel = ({ showAll = false }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => {
      if (r.data && r.data.length > 0) setTestimonials(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (showAll || testimonials.length === 0) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [showAll, testimonials.length]);

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (loading) {
    if (showAll) {
      return (
        <section className="py-24 lg:py-32 bg-white" data-testid="testimonials-section-loading">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="mb-16">
              <p className="accent-font text-lime-600 mb-4">Testimonials</p>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">
                What Our <span className="text-lime-600">Customers</span> Say
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[0, 1, 2].map(i => (
                <div key={i} className="testimonial-card p-8 animate-pulse">
                  <div className="h-10 w-10 bg-neutral-100 rounded mb-4" />
                  <div className="h-4 w-full bg-neutral-100 rounded mb-2" />
                  <div className="h-4 w-5/6 bg-neutral-100 rounded mb-2" />
                  <div className="h-4 w-2/3 bg-neutral-100 rounded mb-6" />
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-neutral-100" />
                    <div className="space-y-2">
                      <div className="h-3 w-24 bg-neutral-100 rounded" />
                      <div className="h-3 w-16 bg-neutral-100 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }
    return (
      <section className="py-24 lg:py-32 bg-neutral-900" data-testid="testimonials-carousel-loading">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <p className="accent-font text-lime-400 mb-4">Testimonials</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
              What Our <span className="text-lime-400">Customers</span> Say
            </h2>
          </div>
          <div className="bg-neutral-800/50 backdrop-blur rounded-2xl border border-white/10 p-8 lg:p-12 min-h-[300px] animate-pulse">
            <div className="space-y-4">
              <div className="h-12 w-12 bg-white/10 rounded" />
              <div className="h-5 w-full bg-white/10 rounded" />
              <div className="h-5 w-5/6 bg-white/10 rounded" />
              <div className="h-5 w-2/3 bg-white/10 rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) return null;

  if (showAll) {
    return (
      <section className="py-24 lg:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <p className="accent-font text-lime-600 mb-4">Testimonials</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">
              What Our <span className="text-lime-600">Customers</span> Say
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="testimonial-card p-8"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <Quotes size={40} weight="fill" className="text-lime-200 mb-4" />
                <p className="text-neutral-600 leading-relaxed mb-6">
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                <div className="flex items-center gap-4">
                  {testimonial.image_url && (
                    <img src={resolveImageUrl(testimonial.image_url)} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                  )}
                  <div>
                    <p className="font-semibold text-neutral-900">{testimonial.name}</p>
                    <p className="text-neutral-500 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const current = testimonials[currentIndex];

  return (
    <section className="py-24 lg:py-32 bg-neutral-900" data-testid="testimonials-carousel">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
          <div>
            <p className="accent-font text-lime-400 mb-4">Testimonials</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
              What Our <span className="text-lime-400">Customers</span> Say
            </h2>
          </div>
          <div className="flex items-center gap-4 mt-8 lg:mt-0">
            <button onClick={handlePrev} className="p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:border-lime-400/50 transition-colors duration-200" data-testid="testimonial-prev-btn" aria-label="Previous testimonial">
              <CaretLeft size={24} />
            </button>
            <button onClick={handleNext} className="p-3 rounded-full border border-white/20 text-white hover:bg-white/10 hover:border-lime-400/50 transition-colors duration-200" data-testid="testimonial-next-btn" aria-label="Next testimonial">
              <CaretRight size={24} />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden min-h-[300px]">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="bg-neutral-800/50 backdrop-blur rounded-2xl border border-white/10 p-8 lg:p-12"
              data-testid="active-testimonial"
            >
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2">
                  <Quotes size={48} weight="fill" className="text-lime-400/30 mb-6" />
                  <p className="text-xl lg:text-2xl text-neutral-200 leading-relaxed mb-8">
                    &ldquo;{current.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-4">
                    {current.image_url && (
                      <img src={resolveImageUrl(current.image_url)} alt={current.name} className="w-14 h-14 rounded-full object-cover border-2 border-lime-400/30" />
                    )}
                    <div>
                      <p className="font-outfit font-semibold text-lg text-white">{current.name}</p>
                      <p className="text-neutral-400">{current.company}</p>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block">
                  {current.image_url && (
                    <img src={resolveImageUrl(current.image_url)} alt={current.name} className="w-full aspect-square rounded-2xl object-cover opacity-60" />
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => { setDirection(index > currentIndex ? 1 : -1); setCurrentIndex(index); }}
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${index === currentIndex ? 'bg-lime-400' : 'bg-neutral-600'}`}
              data-testid={`testimonial-dot-${index}`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
